import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import {
  FileText, Clock, Video, MessagesSquare, NotebookPen, Bot, ListChecks,
  Star, Users, MapPin, Sparkles, Send, Lock, Download, Loader2, ChevronRight, Plus, Pin,
  Link2 as LinkIcon, ExternalLink, Youtube, ThumbsUp, Image as ImageIcon, Trash2, Pencil,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { AIDisclaimer } from "@/components/AIDisclaimer";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiError } from "@/lib/api";
import { ImageUploader, PostImages } from "@/components/ImageUploader";
import { caseDisplayName } from "@/lib/caseName";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BOARDS = ["General Discussion", "New Developments", "Court Documents", "Detective Questions", "Evidence Discussion"];

export function photoUrl(p) {
  if (!p) return null;
  return p.external_url || (p.storage_path ? `${API}/photos/file/${p.storage_path}` : null);
}

function MemberGate({ children, isMember, label }) {
  if (isMember) return children;
  return (
    <div className="card-surface rounded-md p-10 text-center">
      <Lock className="w-8 h-8 text-gold mx-auto mb-4" />
      <h3 className="font-display text-lg font-semibold text-white">Founding Members only</h3>
      <p className="text-sm text-white/50 mt-2">{label}</p>
      <Link to="/pricing" className="mt-6 inline-block px-5 py-2.5 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors text-sm">Subscribe from $4.99/mo</Link>
    </div>
  );
}

function CaseAdminBar({ c, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ title: c.title || "", summary: c.summary || "", status: c.status || "", location: c.location || "", thumbnail: c.thumbnail || "" });
  const isDraft = c.published === false || String(c.status).toLowerCase() === "draft";

  const patch = async (body, okMsg) => {
    setBusy(true);
    try { await api.patch(`/cases/${c.case_id}`, body); toast.success(okMsg); onChange?.(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };

  const save = async () => {
    await patch({ title: form.title, summary: form.summary, status: form.status, location: form.location, thumbnail: form.thumbnail }, "Case updated");
    setOpen(false);
  };
  const publish = () => patch({ published: true, status: (String(c.status).toLowerCase() === "draft" ? "Active" : c.status) }, "Case published — now visible to members");
  const unpublish = () => patch({ published: false }, "Case unpublished (hidden from members)");

  return (
    <>
      {isDraft
        ? <Button data-testid="publish-case-btn" onClick={publish} disabled={busy} className="bg-emerald-500 text-charcoal hover:bg-emerald-400 rounded-sm font-semibold"><Send className="w-4 h-4 mr-1.5" /> Publish</Button>
        : <Button data-testid="unpublish-case-btn" onClick={unpublish} disabled={busy} variant="outline" className="border-white/15 text-white/70 hover:bg-white/5 rounded-sm">Unpublish</Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button data-testid="edit-case-btn" variant="outline" className="border-gold/40 text-gold hover:bg-gold/10 rounded-sm bg-transparent"><Pencil className="w-4 h-4 mr-1.5" /> Edit Case</Button>
        </DialogTrigger>
        <DialogContent className="bg-charcoal border-white/10 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Edit Case</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="label-mono text-xs mb-1 block">Title</label>
              <Input data-testid="edit-case-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-charcoal border-white/15" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label-mono text-xs mb-1 block">Status</label>
                <Input data-testid="edit-case-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} placeholder="Active / Cold Case / Trial" className="bg-charcoal border-white/15" /></div>
              <div><label className="label-mono text-xs mb-1 block">Location</label>
                <Input data-testid="edit-case-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-charcoal border-white/15" /></div>
            </div>
            <div><label className="label-mono text-xs mb-1 block">Summary</label>
              <Textarea data-testid="edit-case-summary" rows={10} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} className="bg-charcoal border-white/15" /></div>
            <div><label className="label-mono text-xs mb-1 block">Banner image URL</label>
              <Input data-testid="edit-case-thumbnail" value={form.thumbnail} onChange={(e) => setForm({ ...form, thumbnail: e.target.value })} className="bg-charcoal border-white/15" /></div>
          </div>
          <DialogFooter>
            <Button data-testid="save-case-btn" onClick={save} disabled={busy} className="bg-gold text-charcoal hover:bg-gold-hover">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


export default function CaseWorkspace() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [c, setCase] = useState(null);
  const [teaser, setTeaser] = useState(null);
  const [tab, setTab] = useState("overview");

  const isAdmin = user?.role === "admin";
  const isMember = c?.is_member;
  const access = c?.access_level;                 // full | free | locked
  const canTools = access === "full";             // member-only case tools
  const canDiscuss = access === "full" || access === "free"; // discussion: members + free-case owner

  const loadCase = useCallback(() => {
    api.get(`/cases/${slug}`)
      .then(({ data }) => { setCase(data); setTeaser(null); })
      .catch((err) => {
        const code = err?.response?.status;
        if (code === 401 || code === 403) {
          api.get(`/public/cases/${slug}`).then(({ data }) => setTeaser(data)).catch(() => navigate("/cases"));
        } else {
          navigate("/cases");
        }
      });
  }, [slug, navigate]);

  useEffect(() => { loadCase(); }, [loadCase]);

  const toggleFollow = async () => {
    try {
      const { data } = await api.post(`/cases/${c.case_id}/follow`);
      const nextAccess = isMember ? "full" : (data.following ? "free" : "locked");
      setCase({ ...c, following: data.following, access_level: nextAccess,
        follower_count: c.follower_count + (data.following ? 1 : -1) });
      toast.success(data.following ? (isMember ? "Following case" : "This is now your free case — jump into the discussion") : "Unfollowed");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not update this case");
    }
  };

  if (!c && !teaser) return <div className="min-h-screen bg-charcoal"><Navbar /><div className="label-mono animate-pulse py-20 text-center">Loading case file…</div></div>;
  if (!c && teaser) return <CaseTeaser t={teaser} loggedIn={!!user} />;

  const tabs = [
    { id: "overview", label: "Overview", icon: ListChecks },
    { id: "timeline", label: "Timeline", icon: Clock },
    { id: "documents", label: "Documents", icon: FileText },
    { id: "photos", label: "Photos", icon: ImageIcon },
    { id: "videos", label: "Videos", icon: Video },
    { id: "community", label: "Community", icon: MessagesSquare },
    { id: "notes", label: "Detective Notes", icon: NotebookPen },
    { id: "ai", label: "AI Assistant", icon: Bot },
  ];

  return (
    <div className="min-h-screen bg-charcoal">
      <Navbar />
      {/* Header */}
      <div className="relative border-b border-white/10">
        <div className="absolute inset-0"><img src={c.thumbnail} alt="" className="w-full h-full object-cover opacity-20" /><div className="absolute inset-0 bg-charcoal/80" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <Link to="/cases" className="label-mono hover:text-gold transition-colors inline-flex items-center gap-1 mb-4">Cases <ChevronRight className="w-3 h-3" /> File</Link>
          <div className="flex flex-col lg:flex-row justify-between gap-5">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-2">
                <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm bg-gold/90 text-charcoal font-bold">{c.status}</span>
                {c.location && <span className="flex items-center gap-1.5 text-xs text-white/50 font-mono"><MapPin className="w-3 h-3" /> {c.location}</span>}
              </div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold text-white leading-tight">{caseDisplayName(c.title)}</h1>
              <div className="mt-4 flex items-center gap-5 text-sm text-white/50 font-mono">
                <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /> {c.follower_count} following</span>
                <span className="flex items-center gap-1.5"><FileText className="w-4 h-4" /> {c.doc_count} docs</span>
                <span className="flex items-center gap-1.5"><Video className="w-4 h-4" /> {c.video_count} videos</span>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {isAdmin && <CaseAdminBar c={c} onChange={loadCase} />}
              {isMember && (
                <Link to={`/cases/${slug}/board`} data-testid="open-evidence-board-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm font-semibold text-sm transition-colors bg-white/5 text-gold border border-gold/40 hover:bg-gold/10">
                  <Pin className="w-4 h-4" /> Evidence Board
                </Link>
              )}
              <button onClick={toggleFollow} data-testid="follow-case-btn"
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-sm font-semibold text-sm transition-colors ${c.following ? "bg-gold/15 text-gold border border-gold/40" : "bg-gold text-charcoal hover:bg-gold-hover"}`}>
                <Star className={`w-4 h-4 ${c.following ? "fill-gold" : ""}`} />
                {c.following ? (isMember ? "Following" : "Your free case") : (isMember ? "Follow Case" : "Make this my free case")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full justify-start overflow-x-auto bg-transparent border-b border-white/10 rounded-none h-auto p-0 gap-1">
            {tabs.map((t) => (
              <TabsTrigger key={t.id} value={t.id} data-testid={`case-tab-${t.id}`}
                className="flex items-center gap-2 px-4 py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-gold data-[state=active]:text-gold data-[state=active]:bg-transparent text-white/50 text-sm font-medium whitespace-nowrap">
                <t.icon className="w-4 h-4" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="py-8">
            {!isMember && access !== "full" && (
              <div className="mb-6 card-surface rounded-md p-4 flex items-start gap-3" data-testid="free-tier-banner">
                <Lock className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                <div className="text-sm text-white/70">
                  {access === "free"
                    ? <>This is your <span className="text-gold font-semibold">free case</span> — read the overview, follow the timeline, and join the discussion. Documents, Detective Notes, AI Assistant, Evidence Board, and the live Bullpen are for Founding Members. <Link to="/pricing" className="text-gold hover:underline">Upgrade →</Link></>
                    : <>Members-only case. <button onClick={toggleFollow} className="text-gold hover:underline" data-testid="make-free-case-inline">Make it your free case</button> to read + post in the discussion, or <Link to="/pricing" className="text-gold hover:underline">become a Founding Member</Link> for every case.</>}
                </div>
              </div>
            )}
            <TabsContent value="overview"><Overview c={c} /></TabsContent>
            <TabsContent value="timeline">{canDiscuss ? <Timeline caseId={c.case_id} isAdmin={isAdmin} /> : <UpgradeLock title="Timeline" desc="Make this your free case to follow the case timeline, or upgrade for every case." free={false} onPick={toggleFollow} picked={c.following} />}</TabsContent>
            <TabsContent value="documents">{canTools ? <Documents caseId={c.case_id} isAdmin={isAdmin} isMember={isMember} onChange={loadCase} /> : <UpgradeLock title="Documents are a member tool" desc="Source documents and filings are for Founding Members." free={access === "free"} onPick={toggleFollow} picked={c.following} />}</TabsContent>
            <TabsContent value="photos">{canTools ? <Photos caseId={c.case_id} isAdmin={isAdmin} onChange={loadCase} /> : <UpgradeLock title="Photos are a member tool" desc="Case photo evidence is for Founding Members." free={access === "free"} onPick={toggleFollow} picked={c.following} />}</TabsContent>
            <TabsContent value="videos">{canTools ? <Videos caseId={c.case_id} isAdmin={isAdmin} /> : <UpgradeLock title="Videos are a member tool" desc="Case video breakdowns are for Founding Members (and on our YouTube channel)." free={access === "free"} onPick={toggleFollow} picked={c.following} />}</TabsContent>
            <TabsContent value="community">{canDiscuss ? <Community caseId={c.case_id} isAdmin={isAdmin} isMember={isMember} /> : <UpgradeLock title="Join the discussion" desc="Make this your free case to read and post here — or upgrade to join the conversation on every case." free={false} onPick={toggleFollow} picked={c.following} />}</TabsContent>
            <TabsContent value="notes">{canTools ? <Notes caseId={c.case_id} isAdmin={isAdmin} isMember={isMember} /> : <UpgradeLock title="Detective Notes are a member tool" desc="Christine's detective notes are for Founding Members." free={access === "free"} onPick={toggleFollow} picked={c.following} />}</TabsContent>
            <TabsContent value="ai">{canTools ? <AIAssistant caseId={c.case_id} isMember={isMember} /> : <UpgradeLock title="AI Assistant is a member tool" desc="The case AI assistant is for Founding Members." free={access === "free"} onPick={toggleFollow} picked={c.following} />}</TabsContent>
          </div>
        </Tabs>
      </div>
    </div>
  );
}

const CATEGORY_NAMES = { active: "Active Cases", missing: "Missing Persons", unidentified: "Unidentified Human Remains", cold: "Cold Cases", wrongful: "Wrongful Convictions", dna: "DNA Cases" };

function UpgradeLock({ title, desc, free, onPick, picked }) {
  return (
    <div className="card-surface rounded-md p-10 text-center max-w-xl mx-auto" data-testid="upgrade-lock">
      <Lock className="w-8 h-8 text-gold mx-auto mb-4" />
      <h3 className="font-display text-xl text-white mb-2">Ready to Go Deeper?</h3>
      <p className="text-white/50 text-sm mb-6">{desc}</p>
      <div className="flex items-center justify-center gap-3 flex-wrap">
        {!free && !picked && onPick && (
          <button onClick={onPick} data-testid="upgrade-lock-pick"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm font-semibold text-sm bg-white/5 text-gold border border-gold/40 hover:bg-gold/10">
            <Star className="w-4 h-4" /> Make this my free case
          </button>
        )}
        <Link to="/pricing" data-testid="upgrade-lock-cta"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-sm font-semibold text-sm bg-gold text-charcoal hover:bg-gold-hover">
          Become a Founding Member
        </Link>
      </div>
    </div>
  );
}

function CaseTeaser({ t, loggedIn }) {
  const joinTo = loggedIn ? "/pricing" : "/register";
  const desc = (t.summary || "").slice(0, 155);
  const url = `https://helpsolvethecase.com/cases/${t.slug}`;
  const cover = photoUrl(t.primary_photo) || t.thumbnail;
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Article",
    headline: t.title, about: t.title,
    articleSection: CATEGORY_NAMES[t.category] || "True Crime",
    description: desc, url, image: cover,
    author: { "@type": "Person", name: "Christine Burke", jobTitle: "Detective & Sergeant (Ret.), Forensic Genetic Genealogist" },
    publisher: { "@type": "Organization", name: "Help Solve The Case" },
  };
  return (
    <div className="min-h-screen bg-charcoal">
      <Helmet>
        <title>{`${t.title} — Case File | Help Solve The Case™`}</title>
        <meta name="description" content={`${t.title}${t.location ? " · " + t.location : ""}. ${desc}`} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${t.title} — Help Solve The Case`} />
        <meta property="og:description" content={desc} />
        <meta property="og:url" content={url} />
        {cover && <meta property="og:image" content={cover} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${t.title} — Help Solve The Case`} />
        <meta name="twitter:description" content={desc} />
        {cover && <meta name="twitter:image" content={cover} />}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Navbar />
      <div className="relative border-b border-white/10">
        <div className="absolute inset-0"><img src={cover} alt="" className="w-full h-full object-cover opacity-20" /><div className="absolute inset-0 bg-charcoal/80" /></div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <Link to="/cases" className="label-mono hover:text-gold transition-colors inline-flex items-center gap-1 mb-4">Cases <ChevronRight className="w-3 h-3" /> File</Link>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-sm bg-gold/90 text-charcoal font-bold">{t.status}</span>
            {t.location && <span className="flex items-center gap-1.5 text-xs text-white/50 font-mono"><MapPin className="w-3 h-3" /> {t.location}</span>}
          </div>
          <h1 className="font-display text-3xl sm:text-5xl font-bold text-white leading-tight">{caseDisplayName(t.title)}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-5 text-sm text-white/50 font-mono">
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /> {t.follower_count} following</span>
            <span className="flex items-center gap-1.5"><FileText className="w-4 h-4" /> {t.doc_count} docs</span>
            <span className="flex items-center gap-1.5"><Video className="w-4 h-4" /> {t.video_count} videos</span>
            <span className="flex items-center gap-1.5"><MessagesSquare className="w-4 h-4" /> {t.thread_count} discussions</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          {cover && (
            <figure className="card-surface rounded-md overflow-hidden" data-testid="teaser-cover-photo">
              <img src={cover} alt={t.primary_photo?.caption || t.title} className="w-full max-h-[420px] object-cover" />
              {t.primary_photo?.caption && <figcaption className="px-5 py-3 text-sm text-white/55 border-t border-white/10 font-mono">{t.primary_photo.caption}</figcaption>}
            </figure>
          )}
          <div>
            <div className="label-mono mb-3">Case Summary</div>
            <p className="text-white/70 leading-relaxed">{t.summary}</p>
          </div>
          {t.timeline_preview?.length > 0 && (
            <div>
              <div className="label-mono mb-3">Key Dates</div>
              <div className="card-surface rounded-md divide-y divide-white/5">
                {t.timeline_preview.slice(0, 6).map((e, i) => (
                  <div key={`${e.date}-${e.title}-${i}`} className="flex items-start gap-3 p-4">
                    <Clock className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    <div><span className="font-mono text-xs text-gold">{e.date}</span><p className="text-sm text-white/80">{e.title}</p></div>
                  </div>
                ))}
              </div>
              {t.timeline_preview.length > 6 && <p className="mt-2 text-xs text-white/30 font-mono">+ {t.timeline_preview.length - 6} more in the full timeline</p>}
            </div>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="card-surface rounded-md p-6 grain sticky top-6">
            <Lock className="w-7 h-7 text-gold mb-3" />
            <h2 className="font-display text-xl font-bold text-white">Open the full case workspace</h2>
            <p className="text-sm text-white/55 mt-2">Become a Founding Member to unlock everything in this file:</p>
            <ul className="mt-4 space-y-2 text-sm text-white/75">
              <li className="flex items-center gap-2"><FileText className="w-4 h-4 text-gold" /> {t.doc_count} documents & court filings</li>
              <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-gold" /> Full case timeline</li>
              <li className="flex items-center gap-2"><Video className="w-4 h-4 text-gold" /> {t.video_count} video breakdowns</li>
              <li className="flex items-center gap-2"><NotebookPen className="w-4 h-4 text-gold" /> {t.note_count} detective notes</li>
              <li className="flex items-center gap-2"><MessagesSquare className="w-4 h-4 text-gold" /> Community discussion</li>
              <li className="flex items-center gap-2"><Bot className="w-4 h-4 text-gold" /> AI case assistant</li>
            </ul>
            <Link to={joinTo} data-testid="teaser-join-btn" className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors text-sm">
              {loggedIn ? "Become a Founding Member" : "Join — from $4.99/mo"} <ChevronRight className="w-4 h-4" />
            </Link>
            {!loggedIn && <p className="mt-3 text-center text-xs text-white/40"><Link to="/login" className="text-gold hover:underline">Already a member? Sign in</Link></p>}
          </div>
        </aside>
      </div>
      <Footer />
    </div>
  );
}

function Overview({ c }) {
  const cover = photoUrl(c.primary_photo);
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        {cover && (
          <figure className="card-surface rounded-md overflow-hidden" data-testid="overview-cover-photo">
            <img src={cover} alt={c.primary_photo?.caption || c.title} className="w-full max-h-[440px] object-cover" />
            {c.primary_photo?.caption && (
              <figcaption className="px-5 py-3 text-sm text-white/55 border-t border-white/10 font-mono">{c.primary_photo.caption}</figcaption>
            )}
          </figure>
        )}
        <div className="card-surface rounded-md p-6">
          <div className="label-mono mb-3">Case Summary</div>
          <p className="text-white/80 leading-relaxed whitespace-pre-line">{c.summary}</p>
        </div>
      </div>
      <div className="space-y-4">
        <div className="card-surface rounded-md p-6">
          <div className="label-mono mb-3">Status</div>
          <div className="text-2xl font-display font-bold text-gold">{c.status}</div>
          {c.location && <div className="mt-3 text-sm text-white/50 flex items-center gap-2"><MapPin className="w-4 h-4" /> {c.location}</div>}
        </div>
        <div className="card-surface rounded-md p-6">
          <div className="label-mono mb-3">Quick Facts</div>
          <ul className="space-y-2 text-sm text-white/70 font-mono">
            <li className="flex justify-between"><span className="text-white/40">Followers</span> {c.follower_count}</li>
            <li className="flex justify-between"><span className="text-white/40">Documents</span> {c.doc_count}</li>
            <li className="flex justify-between"><span className="text-white/40">Photos</span> {c.photo_count ?? 0}</li>
            <li className="flex justify-between"><span className="text-white/40">Videos</span> {c.video_count}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function Photos({ caseId, isAdmin, onChange }) {
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ caption: "", is_primary: false });
  const [uploading, setUploading] = useState(false);
  const [linkForm, setLinkForm] = useState({ url: "", caption: "", is_primary: false });
  const [lightbox, setLightbox] = useState(null);
  const load = useCallback(() => api.get(`/cases/${caseId}/photos`).then(({ data }) => setPhotos(data)), [caseId]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) { toast.error("Choose an image"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("caption", meta.caption); fd.append("is_primary", meta.is_primary);
    try {
      await api.post(`/cases/${caseId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setOpen(false); setFile(null); setMeta({ caption: "", is_primary: false }); load(); onChange?.(); toast.success("Photo added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setUploading(false); }
  };

  const addLink = async () => {
    if (!linkForm.url) { toast.error("Paste an image URL"); return; }
    try {
      await api.post(`/cases/${caseId}/photos/link`, linkForm);
      setLinkOpen(false); setLinkForm({ url: "", caption: "", is_primary: false }); load(); onChange?.(); toast.success("Photo added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const setPrimary = async (id) => {
    try { await api.put(`/cases/${caseId}/photos/${id}/primary`); load(); onChange?.(); toast.success("Cover photo updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/cases/${caseId}/photos/${id}`); load(); onChange?.(); toast.success("Photo removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      {isAdmin && (
        <div className="mb-6 flex flex-wrap gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="upload-photo-btn" className="bg-gold text-charcoal hover:bg-gold-hover rounded-sm"><Plus className="w-4 h-4 mr-1" /> Upload Photo</Button></DialogTrigger>
            <DialogContent className="bg-surface border-white/10 text-white">
              <DialogHeader><DialogTitle className="font-display">Upload Case Photo</DialogTitle></DialogHeader>
              <Input type="file" accept=".png,.jpg,.jpeg,.webp,.gif" onChange={(e) => setFile(e.target.files[0])} className="bg-charcoal border-white/15 file:text-gold" data-testid="photo-file-input" />
              <Input placeholder="Caption (optional)" value={meta.caption} onChange={(e) => setMeta({ ...meta, caption: e.target.value })} className="bg-charcoal border-white/15" data-testid="photo-caption-input" />
              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={meta.is_primary} onChange={(e) => setMeta({ ...meta, is_primary: e.target.checked })} className="accent-gold" data-testid="photo-primary-checkbox" />
                Set as cover photo (shown on Overview & public teaser)
              </label>
              <DialogFooter><Button onClick={upload} disabled={uploading} className="bg-gold text-charcoal hover:bg-gold-hover">{uploading ? "Uploading…" : "Upload"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
            <DialogTrigger asChild><Button data-testid="add-photo-link-btn" variant="outline" className="border-white/15 text-white hover:border-gold/40 rounded-sm bg-transparent"><LinkIcon className="w-4 h-4 mr-1" /> Add by URL</Button></DialogTrigger>
            <DialogContent className="bg-surface border-white/10 text-white">
              <DialogHeader><DialogTitle className="font-display">Add Photo by URL</DialogTitle></DialogHeader>
              <Input placeholder="https://… (direct link to an image)" value={linkForm.url} onChange={(e) => setLinkForm((f) => ({ ...f, url: e.target.value }))} className="bg-charcoal border-white/15" data-testid="photo-link-url-input" />
              <Input placeholder="Caption (optional)" value={linkForm.caption} onChange={(e) => setLinkForm((f) => ({ ...f, caption: e.target.value }))} className="bg-charcoal border-white/15" data-testid="photo-link-caption-input" />
              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={linkForm.is_primary} onChange={(e) => setLinkForm((f) => ({ ...f, is_primary: e.target.checked }))} className="accent-gold" />
                Set as cover photo (shown on Overview & public teaser)
              </label>
              <DialogFooter><Button onClick={addLink} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="save-photo-link-btn">Add Photo</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {photos.length === 0 ? <Empty label="No photos in this case file yet." /> : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {photos.map((p) => {
            const url = photoUrl(p);
            return (
              <div key={p.photo_id} className="card-surface rounded-md overflow-hidden group" data-testid={`photo-${p.photo_id}`}>
                <button onClick={() => setLightbox(url)} className="block w-full aspect-square bg-black/40 overflow-hidden">
                  <img src={url} alt={p.caption || "Case photo"} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                </button>
                <div className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    {p.is_primary
                      ? <span className="label-mono text-gold flex items-center gap-1"><Star className="w-3 h-3 fill-gold" /> Cover</span>
                      : <span className="label-mono text-white/30">Photo</span>}
                    {isAdmin && (
                      <div className="flex items-center gap-1">
                        {!p.is_primary && (
                          <button onClick={() => setPrimary(p.photo_id)} title="Set as cover" data-testid={`set-primary-${p.photo_id}`}
                            className="p-1.5 text-white/40 hover:text-gold transition-colors"><Star className="w-3.5 h-3.5" /></button>
                        )}
                        <button onClick={() => remove(p.photo_id)} title="Delete" data-testid={`delete-photo-${p.photo_id}`}
                          className="p-1.5 text-white/40 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                  {p.caption && <p className="mt-1.5 text-xs text-white/60 line-clamp-2">{p.caption}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)} className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out" data-testid="photo-lightbox">
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-sm" />
        </div>
      )}
    </div>
  );
}

function Timeline({ caseId, isAdmin }) {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ date: "", title: "", description: "" });
  const load = useCallback(() => api.get(`/cases/${caseId}/timeline`).then(({ data }) => setEvents(data)), [caseId]);
  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setEditingId(null); setForm({ date: "", title: "", description: "" }); setOpen(true); };
  const startEdit = (ev) => { setEditingId(ev.event_id); setForm({ date: ev.date || "", title: ev.title || "", description: ev.description || "" }); setOpen(true); };

  const save = async () => {
    if (!form.date || !form.title) { toast.error("Date and title are required"); return; }
    try {
      if (editingId) { await api.put(`/cases/${caseId}/timeline/${editingId}`, form); toast.success("Event updated"); }
      else { await api.post(`/cases/${caseId}/timeline`, form); toast.success("Event added"); }
      setOpen(false); setEditingId(null); setForm({ date: "", title: "", description: "" }); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Remove this timeline event?\n\n${ev.date} — ${ev.title}`)) return;
    try { await api.delete(`/cases/${caseId}/timeline/${ev.event_id}`); load(); toast.success("Event removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      {isAdmin && (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditingId(null); }}>
          <DialogTrigger asChild><Button onClick={openAdd} data-testid="add-timeline-btn" className="mb-6 bg-gold text-charcoal hover:bg-gold-hover rounded-sm"><Plus className="w-4 h-4 mr-1" /> Add Event</Button></DialogTrigger>
          <DialogContent className="bg-surface border-white/10 text-white">
            <DialogHeader><DialogTitle className="font-display">{editingId ? "Edit Timeline Event" : "Add Timeline Event"}</DialogTitle></DialogHeader>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="bg-charcoal border-white/15" data-testid="timeline-date-input" />
            <Input placeholder="Event title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-charcoal border-white/15" data-testid="timeline-title-input" />
            <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-charcoal border-white/15" data-testid="timeline-desc-input" />
            <DialogFooter><Button onClick={save} data-testid="save-timeline-btn" className="bg-gold text-charcoal hover:bg-gold-hover">{editingId ? "Save Changes" : "Save"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {events.length === 0 ? <Empty label="No timeline events yet." /> : (
        <div className="relative pl-6 border-l border-white/10 space-y-8">
          {events.map((e, i) => (
            <motion.div key={e.event_id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.05 }} className="relative group">
              <span className="absolute -left-[31px] top-1 w-3 h-3 rounded-full bg-gold border-2 border-charcoal" />
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="label-mono text-gold">{e.date}{e.updated_at ? " · edited" : ""}</div>
                  <h3 className="font-display text-lg font-semibold text-white mt-1">{e.title}</h3>
                  <p className="text-sm text-white/50 mt-1 leading-relaxed">{e.description}</p>
                </div>
                {isAdmin && (
                  <div className="shrink-0 flex items-center gap-1">
                    <button
                      data-testid={`edit-timeline-${e.event_id}`}
                      onClick={() => startEdit(e)}
                      title="Edit event"
                      className="p-2 rounded-sm text-white/40 hover:text-gold hover:bg-white/5 transition-colors"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      data-testid={`delete-timeline-${e.event_id}`}
                      onClick={() => remove(e)}
                      title="Remove event"
                      className="p-2 rounded-sm text-white/40 hover:text-red-400 hover:bg-white/5 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function Documents({ caseId, isAdmin, isMember, onChange }) {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [meta, setMeta] = useState({ title: "", doc_type: "Court Filing", filing_date: "", add_to_timeline: true });
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkForm, setLinkForm] = useState({ title: "", doc_type: "Court Filing", url: "", filing_date: "", add_to_timeline: true });
  const [summary, setSummary] = useState({ id: null, text: "", loading: false });
  const [selected, setSelected] = useState(() => new Set());
  const [editDoc, setEditDoc] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", doc_type: "Court Filing" });
  const load = useCallback(() => api.get(`/cases/${caseId}/documents`).then(({ data }) => setDocs(data)), [caseId]);

  const restoreIds = async (ids) => {
    try {
      await Promise.all(ids.map((id) => api.post(`/cases/${caseId}/documents/${id}/restore`)));
      load(); onChange?.();
    } catch { toast.error("Could not undo"); }
  };
  const remove = async (d) => {
    try {
      await api.delete(`/cases/${caseId}/documents/${d.document_id}`);
      load(); onChange?.();
      toast.success(`Removed "${d.title}"`, { action: { label: "Undo", onClick: () => restoreIds([d.document_id]) } });
    } catch { toast.error("Could not remove document"); }
  };
  const removeSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    try {
      await api.post(`/cases/${caseId}/documents/bulk-delete`, { document_ids: ids });
      setSelected(new Set()); load(); onChange?.();
      toast.success(`Removed ${ids.length} document${ids.length > 1 ? "s" : ""}`, { action: { label: "Undo", onClick: () => restoreIds(ids) } });
    } catch { toast.error("Could not remove documents"); }
  };
  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const openEdit = (d) => { setEditDoc(d); setEditForm({ title: d.title, doc_type: d.doc_type }); };
  const saveEdit = async () => {
    if (!editForm.title.trim()) { toast.error("Title can't be empty"); return; }
    try {
      await api.patch(`/cases/${caseId}/documents/${editDoc.document_id}`, { title: editForm.title, doc_type: editForm.doc_type });
      setEditDoc(null); load(); onChange?.(); toast.success("Document updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file || !meta.title) { toast.error("Add a file and title"); return; }
    setUploading(true); setProgress(0);
    const CHUNK_SIZE = 900 * 1024;
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    let uploadId;
    try {
      const initRes = await api.post(`/cases/${caseId}/documents/upload/init`, {
        filename: file.name, title: meta.title, doc_type: meta.doc_type,
        filing_date: meta.filing_date || "", add_to_timeline: !!meta.add_to_timeline,
        total_chunks: totalChunks, total_size: file.size,
      });
      uploadId = initRes.data.upload_id;

      let done = 0;
      const sendChunk = async (i) => {
        const start = i * CHUNK_SIZE;
        const blob = file.slice(start, Math.min(start + CHUNK_SIZE, file.size));
        let attempt = 0;
        for (;;) {
          try {
            const fd = new FormData();
            fd.append("index", i);
            fd.append("chunk", blob, `${i}.part`);
            await api.post(`/cases/${caseId}/documents/upload/${uploadId}/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            break;
          } catch (err) {
            if (++attempt >= 3) throw err;
            await new Promise((r) => setTimeout(r, 800 * attempt));
          }
        }
        done += 1;
        setProgress(Math.round((done / totalChunks) * 100));
      };

      const CONCURRENCY = 4;
      let next = 0;
      await Promise.all(
        Array.from({ length: Math.min(CONCURRENCY, totalChunks) }, async () => {
          while (next < totalChunks) { const i = next++; await sendChunk(i); }
        })
      );

      await api.post(`/cases/${caseId}/documents/upload/${uploadId}/complete`);
      setOpen(false); setFile(null); setProgress(0);
      setMeta({ title: "", doc_type: "Court Filing", filing_date: "", add_to_timeline: true });
      load(); onChange?.();
      toast.success(meta.add_to_timeline && meta.filing_date ? "Document uploaded & added to timeline" : "Document uploaded");
    } catch (e) {
      if (uploadId) { try { await api.delete(`/cases/${caseId}/documents/upload/${uploadId}`); } catch (e2) { console.warn("Cleanup of failed upload session failed", e2); } }
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const addLink = async () => {
    if (!linkForm.title || !linkForm.url) { toast.error("Add a title and link"); return; }
    try {
      await api.post(`/cases/${caseId}/documents/link`, linkForm);
      setLinkOpen(false); setLinkForm({ title: "", doc_type: "Court Filing", url: "", filing_date: "", add_to_timeline: true }); load(); onChange?.(); toast.success(linkForm.add_to_timeline && linkForm.filing_date ? "Document link added & added to timeline" : "Document link added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const summarize = async (doc) => {
    setSummary({ id: doc.document_id, text: "", loading: true });
    try {
      const { data } = await api.post(`/documents/${doc.document_id}/summarize`);
      setSummary({ id: doc.document_id, text: data.summary, loading: false });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setSummary({ id: null, text: "", loading: false }); }
  };

  const DOC_TYPES = ["Court Filing", "Affidavit", "Police Report", "Search Warrant", "Motion", "Transcript", "Document"];

  return (
    <div>
      {isAdmin && (
        <div className="mb-6 flex flex-wrap gap-2">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="upload-doc-btn" className="bg-gold text-charcoal hover:bg-gold-hover rounded-sm"><Plus className="w-4 h-4 mr-1" /> Upload Document</Button></DialogTrigger>
            <DialogContent className="bg-surface border-white/10 text-white">
              <DialogHeader><DialogTitle className="font-display">Upload Case Document</DialogTitle></DialogHeader>
              <Input placeholder="Document title" value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} className="bg-charcoal border-white/15" />
              <Select value={meta.doc_type} onValueChange={(v) => setMeta({ ...meta, doc_type: v })}>
                <SelectTrigger className="bg-charcoal border-white/15"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-white">{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="file" accept=".pdf,.txt,.png,.jpg,.jpeg,.docx,.xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files[0])} className="bg-charcoal border-white/15 file:text-gold" data-testid="doc-file-input" />
              <p className="text-xs text-white/40 -mt-1">Large files are uploaded in pieces, and PDFs are compressed automatically. Big filings may take a few minutes.</p>
              <div>
                <label className="label-mono block mb-1.5">Filing date <span className="text-white/30">(optional)</span></label>
                <Input type="date" value={meta.filing_date} onChange={(e) => setMeta({ ...meta, filing_date: e.target.value })} className="bg-charcoal border-white/15" data-testid="doc-filing-date-input" />
              </div>
              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={meta.add_to_timeline} onChange={(e) => setMeta({ ...meta, add_to_timeline: e.target.checked })} className="accent-gold" data-testid="doc-add-timeline-checkbox" />
                Add this filing to the case timeline (uses the filing date)
              </label>
              <DialogFooter>
                <div className="w-full">
                  {uploading && (
                    <div className="mb-3" data-testid="doc-upload-progress">
                      <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-gold transition-[width] duration-200" style={{ width: `${progress}%` }} />
                      </div>
                      <div className="label-mono mt-1 text-white/50">{progress < 100 ? `Uploading… ${progress}%` : "Finishing & compressing…"}</div>
                    </div>
                  )}
                  <Button onClick={upload} disabled={uploading} className="bg-gold text-charcoal hover:bg-gold-hover w-full">{uploading ? `Uploading… ${progress}%` : "Upload"}</Button>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
            <DialogTrigger asChild><Button data-testid="add-doc-link-btn" variant="outline" className="border-white/15 text-white hover:border-gold/40 rounded-sm bg-transparent"><LinkIcon className="w-4 h-4 mr-1" /> Add Link</Button></DialogTrigger>
            <DialogContent className="bg-surface border-white/10 text-white">
              <DialogHeader><DialogTitle className="font-display">Add Document by Link</DialogTitle></DialogHeader>
              <p className="text-xs text-white/40 -mt-1">For court filings or records hosted elsewhere that you can&apos;t download. Members can open the link, and AI summary will read the page when possible.</p>
              <Input placeholder="Document title" value={linkForm.title} onChange={(e) => setLinkForm((f) => ({ ...f, title: e.target.value }))} className="bg-charcoal border-white/15" data-testid="doc-link-title-input" />
              <Select value={linkForm.doc_type} onValueChange={(v) => setLinkForm((f) => ({ ...f, doc_type: v }))}>
                <SelectTrigger className="bg-charcoal border-white/15"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-surface border-white/10 text-white">{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Input placeholder="https://… (link to the document)" value={linkForm.url} onChange={(e) => setLinkForm((f) => ({ ...f, url: e.target.value }))} className="bg-charcoal border-white/15" data-testid="doc-link-url-input" />
              <div>
                <label className="label-mono block mb-1.5">Filing date <span className="text-white/30">(optional)</span></label>
                <Input type="date" value={linkForm.filing_date} onChange={(e) => setLinkForm((f) => ({ ...f, filing_date: e.target.value }))} className="bg-charcoal border-white/15" data-testid="doc-link-filing-date-input" />
              </div>
              <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                <input type="checkbox" checked={linkForm.add_to_timeline} onChange={(e) => setLinkForm((f) => ({ ...f, add_to_timeline: e.target.checked }))} className="accent-gold" data-testid="doc-link-add-timeline-checkbox" />
                Add this filing to the case timeline (uses the filing date)
              </label>
              <DialogFooter><Button onClick={addLink} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="save-doc-link-btn">Add Link</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
      {isAdmin && selected.size > 0 && (
        <div className="mb-4 flex items-center gap-3 rounded-sm border border-gold/30 bg-gold/5 px-4 py-2.5" data-testid="doc-bulk-bar">
          <span className="text-sm text-white/80">{selected.size} selected</span>
          <button onClick={removeSelected} data-testid="bulk-remove-docs-btn"
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-red-400/40 rounded-sm text-red-400 hover:bg-red-400/10 transition-colors">
            <Trash2 className="w-3.5 h-3.5" /> Remove selected
          </button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-white/50 hover:text-white/80">Clear</button>
        </div>
      )}
      {docs.length === 0 ? <Empty label="No documents uploaded yet." /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {docs.map((d) => (
            <div key={d.document_id} className="card-surface rounded-md p-5 relative" data-testid={`doc-${d.document_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  {isAdmin && (
                    <input type="checkbox" checked={selected.has(d.document_id)} onChange={() => toggleSelect(d.document_id)}
                      className="mt-1 accent-gold w-4 h-4 cursor-pointer" data-testid={`select-doc-${d.document_id}`} />
                  )}
                  <div className="w-10 h-10 rounded-sm bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0"><FileText className="w-5 h-5 text-gold" /></div>
                  <div>
                    <h3 className="font-display font-semibold text-white leading-tight">{d.title}</h3>
                    <div className="mt-1 label-mono">{d.doc_type} · {d.external_url ? "External link" : `${(d.size / 1024).toFixed(0)} KB`}</div>
                  </div>
                </div>
                {d.external_url && <span className="label-mono text-gold border border-gold/30 rounded-sm px-1.5 py-0.5 shrink-0">LINK</span>}
              </div>
              <div className="mt-4 flex items-center gap-2">
                <a href={d.external_url || `${API}/files/${d.storage_path}`} target="_blank" rel="noreferrer" data-testid={`view-doc-${d.document_id}`}
                  className="flex items-center gap-1.5 text-xs px-3 py-2 border border-white/10 rounded-sm text-white/70 hover:border-gold/40 transition-colors">
                  {d.external_url ? <><ExternalLink className="w-3.5 h-3.5" /> Open Link</> : <><Download className="w-3.5 h-3.5" /> View</>}
                </a>
                {isMember ? (
                  <button onClick={() => summarize(d)} data-testid={`summarize-doc-${d.document_id}`}
                    className="flex items-center gap-1.5 text-xs px-3 py-2 bg-gold/10 border border-gold/30 rounded-sm text-gold hover:bg-gold/20 transition-colors">
                    {summary.id === d.document_id && summary.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                    {d.ai_processed ? "View AI Summary" : "Summarize with AI"}
                  </button>
                ) : (
                  <Link to="/pricing" className="flex items-center gap-1.5 text-xs px-3 py-2 border border-white/10 rounded-sm text-white/40"><Lock className="w-3.5 h-3.5" /> AI for members</Link>
                )}
                {isAdmin && (
                  <div className="ml-auto flex items-center gap-2">
                    <button onClick={() => openEdit(d)} title="Edit document" data-testid={`edit-doc-${d.document_id}`}
                      className="flex items-center gap-1.5 text-xs px-3 py-2 border border-white/10 rounded-sm text-white/60 hover:text-gold hover:border-gold/40 transition-colors">
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button onClick={() => remove(d)} title="Remove document" data-testid={`delete-doc-${d.document_id}`}
                      className="flex items-center gap-1.5 text-xs px-3 py-2 border border-white/10 rounded-sm text-white/50 hover:text-red-400 hover:border-red-400/40 transition-colors">
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </div>
                )}
              </div>
              {summary.id === d.document_id && summary.text && (
                <div className="mt-4">
                  <AIDisclaimer />
                  <div className="mt-2 text-sm text-white/70 leading-relaxed whitespace-pre-line bg-charcoal rounded-sm p-4 border border-white/10 font-mono text-[13px]">{summary.text}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!editDoc} onOpenChange={(o) => { if (!o) setEditDoc(null); }}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader><DialogTitle className="font-display">Edit Document</DialogTitle></DialogHeader>
          <label className="label-mono block mb-1.5">Title</label>
          <Input value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} className="bg-charcoal border-white/15" data-testid="edit-doc-title-input" />
          <label className="label-mono block mb-1.5 mt-3">Type</label>
          <Select value={editForm.doc_type} onValueChange={(v) => setEditForm((f) => ({ ...f, doc_type: v }))}>
            <SelectTrigger className="bg-charcoal border-white/15" data-testid="edit-doc-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter><Button onClick={saveEdit} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="save-doc-edit-btn">Save Changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Videos({ caseId, isAdmin }) {
  const [videos, setVideos] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", youtube_id: "", description: "" });
  const load = useCallback(() => api.get(`/cases/${caseId}/videos`).then(({ data }) => setVideos(data)), [caseId]);
  useEffect(() => { load(); }, [load]);

  const parseId = (v) => {
    const m = v.match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
    return m ? m[1] : v;
  };
  const add = async () => {
    try { await api.post(`/cases/${caseId}/videos`, { ...form, youtube_id: parseId(form.youtube_id) }); setOpen(false); setForm({ title: "", youtube_id: "", description: "" }); load(); toast.success("Video added"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Remove this video?\n\n${v.title}`)) return;
    try { await api.delete(`/cases/${caseId}/videos/${v.video_id}`); load(); toast.success("Video removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-video-btn" className="mb-6 bg-gold text-charcoal hover:bg-gold-hover rounded-sm"><Plus className="w-4 h-4 mr-1" /> Add Video</Button></DialogTrigger>
          <DialogContent className="bg-surface border-white/10 text-white">
            <DialogHeader><DialogTitle className="font-display">Add Video</DialogTitle></DialogHeader>
            <Input placeholder="Video title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-charcoal border-white/15" />
            <Input placeholder="YouTube URL or ID" value={form.youtube_id} onChange={(e) => setForm({ ...form, youtube_id: e.target.value })} className="bg-charcoal border-white/15" />
            <Textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-charcoal border-white/15" />
            <DialogFooter><Button onClick={add} className="bg-gold text-charcoal hover:bg-gold-hover">Add</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {videos.length === 0 ? <Empty label="No videos added yet." /> : (
        <div className="grid md:grid-cols-2 gap-6">
          {videos.map((v) => (
            <div key={v.video_id} className="card-surface rounded-md overflow-hidden">
              <div className="aspect-video bg-black">
                <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${v.youtube_id}`} title={v.title} allowFullScreen frameBorder="0" />
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-display font-semibold text-white">{v.title}</h3>
                  {isAdmin && (
                    <button
                      data-testid={`delete-video-${v.video_id}`}
                      onClick={() => remove(v)}
                      title="Remove video"
                      className="shrink-0 p-1.5 rounded-sm text-white/40 hover:text-red-400 hover:bg-white/5 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                {v.description ? <p className="text-sm text-white/50 mt-1">{v.description}</p> : null}
                <div className="mt-4 flex flex-wrap items-center gap-2 rounded-sm border border-gold/30 bg-gold/5 px-3 py-2.5">
                  <ThumbsUp className="w-4 h-4 text-gold shrink-0" />
                  <span className="text-xs text-white/70 mr-auto">Enjoying this breakdown? Like it & subscribe for new case updates.</span>
                  <a
                    data-testid={`video-like-link-${v.video_id}`}
                    href={`https://www.youtube.com/watch?v=${v.youtube_id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-sm bg-white/10 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-white/20 transition-colors"
                  >
                    <ThumbsUp className="w-3.5 h-3.5" /> Like
                  </a>
                  <a
                    data-testid={`video-subscribe-link-${v.video_id}`}
                    href="https://www.youtube.com/@helpsolvethecase?sub_confirmation=1"
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-sm bg-[#FF0000] px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-[#cc0000] transition-colors"
                  >
                    <Youtube className="w-3.5 h-3.5" /> Subscribe
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Community({ caseId, isAdmin, isMember }) {
  const [board, setBoard] = useState(BOARDS[0]);
  const [threads, setThreads] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const [images, setImages] = useState([]);
  const navigate = useNavigate();
  const load = useCallback(() => api.get(`/cases/${caseId}/threads`, { params: { board } }).then(({ data }) => setThreads(data)), [caseId, board]);
  useEffect(() => { load(); }, [load]);

  const post = async () => {
    try { await api.post(`/cases/${caseId}/threads`, { ...form, board, images }); setOpen(false); setForm({ title: "", body: "" }); setImages([]); load(); toast.success("Thread posted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pin = async (id) => { await api.post(`/threads/${id}/pin`); load(); };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        {BOARDS.map((b) => (
          <button key={b} onClick={() => setBoard(b)} data-testid={`board-${b.replace(/\s/g, "-")}`}
            className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${board === b ? "bg-gold text-charcoal border-gold" : "border-white/10 text-white/60 hover:border-gold/40"}`}>{b}</button>
        ))}
      </div>

      {isMember ? (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-thread-btn" className="mb-6 bg-gold text-charcoal hover:bg-gold-hover rounded-sm"><Plus className="w-4 h-4 mr-1" /> New Discussion</Button></DialogTrigger>
          <DialogContent className="bg-surface border-white/10 text-white">
            <DialogHeader><DialogTitle className="font-display">Start a discussion in {board}</DialogTitle></DialogHeader>
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-charcoal border-white/15" data-testid="thread-title-input" />
            <Textarea placeholder="Share your evidence-based analysis. No accusations or personal info." rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="bg-charcoal border-white/15" data-testid="thread-body-input" />
            <ImageUploader images={images} setImages={setImages} />
            <p className="text-[11px] font-mono text-white/40">Reminder: educational analysis only. No harassment, doxxing, or unsupported accusations.</p>
            <DialogFooter><Button onClick={post} className="bg-gold text-charcoal hover:bg-gold-hover" data-testid="post-thread-btn">Post</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      ) : (
        <div className="mb-6"><MemberGate isMember={false} label="Become a member to start and join discussions." /></div>
      )}

      {threads.length === 0 ? <Empty label="No discussions in this board yet." /> : (
        <div className="space-y-3">
          {threads.map((t) => (
            <div key={t.thread_id} className="card-surface rounded-md p-5 cursor-pointer" onClick={() => navigate(`/threads/${t.thread_id}`)} data-testid={`thread-${t.thread_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    {t.has_unread && <span className="w-2 h-2 rounded-full bg-gold shrink-0" data-testid={`thread-unread-${t.thread_id}`} title="New activity" />}
                    {t.pinned && <Pin className="w-3.5 h-3.5 text-gold" />}
                    <h3 className="font-display font-semibold text-white hover:text-gold transition-colors">{t.title}</h3>
                  </div>
                  <p className="text-sm text-white/50 mt-1 line-clamp-2">{t.body}</p>
                  <div className="mt-3 flex items-center gap-4 label-mono">
                    <span className={t.author_role === "admin" ? "text-gold" : ""}>{t.author_name}{t.author_role === "admin" && " · Detective"}</span>
                    <span className="flex items-center gap-1"><MessagesSquare className="w-3 h-3" /> {t.comment_count}</span>
                    <span className="flex items-center gap-1"><Star className="w-3 h-3" /> {t.upvote_count}</span>
                  </div>
                </div>
                {isAdmin && <button onClick={(e) => { e.stopPropagation(); pin(t.thread_id); }} className="text-white/40 hover:text-gold" data-testid={`pin-${t.thread_id}`}><Pin className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Notes({ caseId, isAdmin, isMember }) {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", body: "" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", body: "" });
  const load = useCallback(() => api.get(`/cases/${caseId}/notes`).then(({ data }) => setNotes(data)), [caseId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    try { await api.post(`/cases/${caseId}/notes`, form); setOpen(false); setForm({ title: "", body: "" }); load(); toast.success("Note published"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEdit = (n) => { setEditingId(n.note_id); setEditForm({ title: n.title, body: n.body }); };
  const cancelEdit = () => { setEditingId(null); setEditForm({ title: "", body: "" }); };
  const saveEdit = async (noteId) => {
    try { await api.put(`/cases/${caseId}/notes/${noteId}`, editForm); cancelEdit(); load(); toast.success("Note updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!isMember && !isAdmin) return <MemberGate isMember={false} label="Detective Notes are available to Founding Members." />;

  return (
    <div>
      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-note-btn" className="mb-6 bg-gold text-charcoal hover:bg-gold-hover rounded-sm"><Plus className="w-4 h-4 mr-1" /> Write Note</Button></DialogTrigger>
          <DialogContent className="bg-surface border-white/10 text-white">
            <DialogHeader><DialogTitle className="font-display">Detective Note</DialogTitle></DialogHeader>
            <Input placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-charcoal border-white/15" />
            <Textarea placeholder="Your observations…" rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="bg-charcoal border-white/15" />
            <DialogFooter><Button onClick={add} className="bg-gold text-charcoal hover:bg-gold-hover">Publish</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {notes.length === 0 ? <Empty label="No detective notes yet." /> : (
        <div className="space-y-4">
          {notes.map((n) => (
            <div key={n.note_id} className="card-surface rounded-md p-6 border-l-2 border-l-gold">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-gold/15 border border-gold/30 flex items-center justify-center"><NotebookPen className="w-4 h-4 text-gold" /></div>
                <div><div className="text-sm font-semibold text-white">{n.author}</div><div className="label-mono">Detective &amp; Sergeant (Ret.) · Forensic Genetic Genealogist (FGG®)</div></div>
              </div>
              {editingId === n.note_id ? (
                <div className="mt-3 space-y-3">
                  <Input data-testid="edit-note-title-input" placeholder="Title" value={editForm.title} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} className="bg-charcoal border-white/15" />
                  <Textarea data-testid="edit-note-body-input" placeholder="Your observations…" rows={6} value={editForm.body} onChange={(e) => setEditForm({ ...editForm, body: e.target.value })} className="bg-charcoal border-white/15" />
                  <div className="flex gap-2">
                    <Button data-testid="save-note-btn" onClick={() => saveEdit(n.note_id)} className="bg-gold text-charcoal hover:bg-gold-hover rounded-sm">Save</Button>
                    <Button data-testid="cancel-note-btn" onClick={cancelEdit} variant="outline" className="border-white/15 text-white/70 hover:bg-white/5 rounded-sm">Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between gap-3 mt-3">
                    <h3 className="font-display text-lg font-semibold text-white">{n.title}</h3>
                    {isAdmin && (
                      <Button data-testid="edit-note-btn" onClick={() => startEdit(n)} variant="ghost" size="sm" className="text-white/50 hover:text-gold hover:bg-transparent shrink-0"><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
                    )}
                  </div>
                  <p className="text-sm text-white/70 mt-2 leading-relaxed whitespace-pre-line">{n.body}</p>
                  {n.updated_at && <div className="label-mono mt-3 text-white/40">Edited</div>}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AIAssistant({ caseId, isMember }) {
  const [history, setHistory] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (isMember) api.get(`/cases/${caseId}/ai/history`).then(({ data }) => setHistory(data.reverse())).catch(() => {}); }, [caseId, isMember]);

  if (!isMember) return <MemberGate isMember={false} label="The AI Assistant is a Founding Member feature." />;

  const ask = async () => {
    if (!q.trim()) return;
    const question = q; setQ(""); setLoading(true);
    setHistory((h) => [...h, { question, answer: null, pending: true }]);
    try {
      const { data } = await api.post(`/cases/${caseId}/ai/ask`, { question });
      setHistory((h) => h.map((m) => m.pending ? { question, answer: data.answer } : m));
    } catch (e) {
      setHistory((h) => h.filter((m) => !m.pending));
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const SUGGESTIONS = ["Summarize the documented timeline of this case.", "Explain what a motion to suppress means.", "What is chain of custody and why does it matter?", "Generate 3 educational discussion questions."];

  return (
    <div className="max-w-3xl">
      <div className="card-surface rounded-md p-5 mb-4">
        <div className="flex items-center gap-2 mb-3"><Bot className="w-5 h-5 text-gold" /><h3 className="font-display font-semibold text-white">Case AI Assistant</h3></div>
        <AIDisclaimer />
      </div>

      <div className="space-y-4 mb-6">
        {history.length === 0 && (
          <div className="grid sm:grid-cols-2 gap-2">
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => setQ(s)} className="text-left text-sm p-3 card-surface rounded-md text-white/60 hover:text-gold transition-colors" data-testid="ai-suggestion">{s}</button>
            ))}
          </div>
        )}
        {history.map((m, i) => (
          <div key={`${m.conv_id || m.question}-${i}`}>
            <div className="flex justify-end mb-2"><div className="bg-gold/15 border border-gold/30 rounded-md px-4 py-2.5 text-sm text-white max-w-[85%]">{m.question}</div></div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-sm bg-gold/10 border border-gold/20 flex items-center justify-center shrink-0"><Bot className="w-4 h-4 text-gold" /></div>
              <div className="card-surface rounded-md px-4 py-3 text-sm text-white/80 leading-relaxed whitespace-pre-line flex-1">
                {m.pending ? <span className="flex items-center gap-2 text-white/40"><Loader2 className="w-4 h-4 animate-spin" /> Analyzing case materials…</span> : m.answer}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-4 flex gap-2 card-surface rounded-md p-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !loading && ask()}
          placeholder="Ask an educational question about this case…" className="bg-charcoal border-white/15 text-white" data-testid="ai-input" />
        <Button onClick={ask} disabled={loading} className="bg-gold text-charcoal hover:bg-gold-hover shrink-0" data-testid="ai-send-btn"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

function Empty({ label }) {
  return <div className="card-surface rounded-md p-12 text-center text-white/40 text-sm">{label}</div>;
}
